import { getUserInfoQuery } from '~/apis/auth';
import {
  AuthProtocolsGetUserInfoResponse,
  MarqCommonEnumsClientClientRoleEnum,
  MarqCommonEnumsClientClientTypeEnum,
  MarqCommonEnumsClientEmployeeAccessPageEnum,
} from '~/swagger/data-contracts';
import { isAllowedAiGeneratedContent, isAllowedAllSocialMedia, isAllowedPiracyAssetsAsSecondary } from '~/utils';

// note-@blue-poolyum: 페이지 접근 제어는 piracy 고객에게만 적용
const hasPageAccess = (
  userInfo: AuthProtocolsGetUserInfoResponse | undefined,
  page: MarqCommonEnumsClientEmployeeAccessPageEnum
): boolean => {
  if (userInfo?.customer_type.primary !== MarqCommonEnumsClientClientTypeEnum.Piracy) {
    return true;
  }

  const allowedPages = userInfo.limited_access_policy?.pages;

  // null이면 모든 페이지 접근 가능 (제한 없음)
  if (!allowedPages || allowedPages.length === 0) {
    return true;
  }

  // 허용된 페이지 목록에 포함되어 있는지 확인
  return allowedPages.includes(page);
};

const hasPerformanceReportPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.role !== MarqCommonEnumsClientClientRoleEnum.LimitedAccess;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Analytics);
  return hasFeatureAccess && hasPagePermission;
};
const hasPerformanceBrandExposurePageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_landscaping;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Analytics);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioAssetsPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess =
    !(authData?.use_piracy || isAllowedPiracyAssetsAsSecondary(authData)) &&
    (authData?.use_ecommerce || authData?.use_social_media || !authData?.use_stand_alone);
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioCounterfeitAssetsPageAccess = async () => {
  const authData = await getUserInfoQuery();

  const hasFeatureAccess =
    (authData?.use_piracy || isAllowedPiracyAssetsAsSecondary(authData)) &&
    (authData?.use_ecommerce || authData?.use_social_media || !authData?.use_stand_alone);
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioPiracyAssetsPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_piracy || isAllowedPiracyAssetsAsSecondary(authData);
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioInfringingImagesPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_ecommerce || authData?.use_social_media || !authData?.use_stand_alone;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioAuthorizedSellersPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess =
    authData?.use_ecommerce || authData?.use_social_media || authData?.use_stand_alone || authData?.use_domain_name;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};
const hasPortfolioModelsPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = !authData?.use_piracy; // NOT use_piracy = allowed
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
  return hasFeatureAccess && hasPagePermission;
};

const hasMarketplacePageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_ecommerce;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Marketplace);
  return hasFeatureAccess && hasPagePermission;
};
const hasAllSocialMediaPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = isAllowedAllSocialMedia(authData);
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.SocialMedia);
  return hasFeatureAccess && hasPagePermission;
};
const hasSocialMediaPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_social_media && !authData?.use_piracy;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.SocialMedia);
  return hasFeatureAccess && !isAllowedAllSocialMedia(authData) && hasPagePermission;
};
const hasPiracySocialMediaPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_piracy;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.SocialMedia);
  return hasFeatureAccess && !isAllowedAllSocialMedia(authData) && hasPagePermission;
};
const hasWebsiteContentPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_stand_alone || authData?.use_piracy;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.WebsiteContent);
  return hasFeatureAccess && hasPagePermission;
};
const hasDomainNamesPageAccess = async () => {
  const authData = await getUserInfoQuery();
  const hasFeatureAccess = authData?.use_domain_name;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.DomainNames);
  return hasFeatureAccess && hasPagePermission;
};
const hasSellerPagesAccess = async () => {
  const authData = await getUserInfoQuery();

  // [PROD-9620]  role == limited access 인 경우에는 모든 브랜드+모든 국가에 권한이 있을 때에만 이 화면에 접근 가능.
  // [PROD-13123] 다만 read only === true 일 때는 무조건 막는다
  const isAllowedRole =
    authData?.role === MarqCommonEnumsClientClientRoleEnum.LimitedAccess
      ? !authData?.limited_access_policy?.has_readonly_access &&
        authData?.limited_access_policy?.has_all_brand_access &&
        authData?.limited_access_policy?.has_all_country_access
      : true;

  const isAllowedActiveDomainType = authData?.use_ecommerce || authData?.use_social_media;

  return isAllowedRole && isAllowedActiveDomainType;
};

const hasSettingTabAccess = async () => {
  const authData = await getUserInfoQuery();
  const isAllowedBillingTabRole = authData?.role !== MarqCommonEnumsClientClientRoleEnum.LimitedAccess;
  const isAllowedPeopleTabRole = authData?.role
    ? [MarqCommonEnumsClientClientRoleEnum.Admin, MarqCommonEnumsClientClientRoleEnum.Owner].includes(authData.role)
    : false;
  const isAllowedCreditsTabRole = !!authData?.reporting_cap_client;

  return {
    billing: isAllowedBillingTabRole,
    people: isAllowedPeopleTabRole,
    credits: isAllowedCreditsTabRole,
  };
};

const hasPriceMonitoringPageAccess = async () => {
  const authData = await getUserInfoQuery();

  const isMAPEnabled = authData?.use_map_monitoring;
  const isAllowedRole =
    authData?.role === MarqCommonEnumsClientClientRoleEnum.LimitedAccess
      ? !authData?.limited_access_policy?.has_readonly_access &&
        authData?.limited_access_policy?.has_all_brand_access &&
        authData?.limited_access_policy?.has_all_country_access
      : true;

  const isAllowed = isMAPEnabled && isAllowedRole;
  return isAllowed;
};

const hasConversationalAssistantPageAccess = async () => {
  const authData = await getUserInfoQuery();

  const isCVAssistantEnabled = authData?.use_cv_assistant;
  const isAllowedRole =
    authData?.role === MarqCommonEnumsClientClientRoleEnum.LimitedAccess
      ? !authData?.limited_access_policy?.has_readonly_access &&
        authData?.limited_access_policy?.has_all_brand_access &&
        authData?.limited_access_policy?.has_all_country_access
      : true;

  const isAllowed = isCVAssistantEnabled && isAllowedRole;
  return isAllowed;
};

const hasGenerativeAIPageAccess = async () => {
  const authData = await getUserInfoQuery();

  const hasFeatureAccess = authData?.use_piracy;
  const hasPagePermission = hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.AiContentTakedown);
  return (hasFeatureAccess && hasPagePermission) || isAllowedAiGeneratedContent(authData);
};

const hasHomePageAccess = async () => {
  const authData = await getUserInfoQuery();
  return hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Home);
};

const hasAnalyticsPageAccess = async () => {
  const authData = await getUserInfoQuery();
  return hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Analytics);
};

const hasPortfolioPageAccess = async () => {
  const authData = await getUserInfoQuery();
  return hasPageAccess(authData, MarqCommonEnumsClientEmployeeAccessPageEnum.Portfolio);
};

export const PermissionMap = {
  Home: hasHomePageAccess,
  Analytics: hasAnalyticsPageAccess,
  Portfolio: hasPortfolioPageAccess,
  PerformanceReport: hasPerformanceReportPageAccess,
  PerformanceBrandExposure: hasPerformanceBrandExposurePageAccess,
  PortfolioAssets: hasPortfolioAssetsPageAccess,
  PortfolioCounterfeitAssets: hasPortfolioCounterfeitAssetsPageAccess,
  PortfolioPiracyAssets: hasPortfolioPiracyAssetsPageAccess,
  PortfolioInfringingImages: hasPortfolioInfringingImagesPageAccess,
  PortfolioAuthorizedSellers: hasPortfolioAuthorizedSellersPageAccess,
  PortfolioModels: hasPortfolioModelsPageAccess,
  Marketplace: hasMarketplacePageAccess,
  AllSocialMedia: hasAllSocialMediaPageAccess,
  SocialMedia: hasSocialMediaPageAccess,
  PiracySocialMedia: hasPiracySocialMediaPageAccess,
  WebsiteContent: hasWebsiteContentPageAccess,
  DomainNames: hasDomainNamesPageAccess,
  Seller: hasSellerPagesAccess,
  PriceMonitoring: hasPriceMonitoringPageAccess,
  SettingTab: hasSettingTabAccess,
  ConversationalAssistant: hasConversationalAssistantPageAccess,
  GenerativeAI: hasGenerativeAIPageAccess,
};
